import { useState, useEffect } from 'react'
import { Search, Button } from 'carbon-components-react'
import { toaster } from '../../helpers/toaster';
import AttachmentsGrid from './AttachmentsGrid'
import AttachmentDetailModal from './AttachmentDetailModal'
import ConfirmModal from '../../components/Modal/ConfirmModal'
// api
import {
  addAttachment,
  updateAttachment,
  deleteAttachment,
} from '../../api/attachments'

const useAttachmentsGrid = ({
  isStudyDetail,
  attachmentList,
  setStudyDetails,
  studyPgId,
  studyOrgStudyId,
  studyUid,
  refreshStudyData,
  studyListOptions,
} = {}) => {
  // const [attachmentList, setAttachmentList] = useState([])
  const [attachmentId, setAttachmentId] = useState(null)
  const [search, setSearch] = useState("")
  const [searchGrid, setSearchGrid] = useState("")
  const [refreshData, setRefreshData] = useState(false)
  const [sort, setSort] = useState("")
  // loading is for initial component preloading
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  // update modal params
  const [detailModalMode, setDetailModalMode] = useState("")
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false)
  const [attachmentData, setAttachmentData] = useState(null)
  // delete modal params
  const [openDeleteModal, setOpenDeleteModal] = useState("")
  const [deleteItem, setDeleteItem] = useState(null)

  const addAttachmentBtnStyle = {
    padding: "11px 18px",
    height: "auto",
    minHeight: "unset",
    borderRadius: "10px",
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "13.5px"
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchGrid(search)
    }, 500)
    // if this effect run again, because `value` changed, we remove the previous timeout
    return () => clearTimeout(timeout)
  }, [search])

  const addNewAttachment = () => {
    setDetailModalMode("create")
    setAttachmentData(null)
    setOpenAttachmentModal(true)
  }

  const attachmentRemoveRow = (itemId) => {
    setOpenDeleteModal(true)
    setDeleteItem(itemId)
  }

  const confirmSave = (formData) => {
    setAttachmentsLoading(true);

    return addAttachment(formData).then(res => {
      // for grid on study details page
      if (isStudyDetail && refreshStudyData && studyUid) {
        // Refresh the full study data from the server using UID
        refreshStudyData(studyUid).then(() => {
          toaster("Attachment added successfully", "success")
          setAttachmentsLoading(false);
          setOpenAttachmentModal(false);
        })
      } else if (attachmentList && setStudyDetails && res.data) {
        // Fallback: update local state if no refresh function provided
        setStudyDetails({
          type: 'ADD_ITEM',
          itemType: 'attachments',
          item: res.data,
        })
        toaster("Attachment added successfully", "success")
        setAttachmentsLoading(false);
        setOpenAttachmentModal(false);
      } else {
        // for attachment list page
        setRefreshData(true)
        toaster("Attachment added successfully", "success")
        setAttachmentsLoading(false);
        setOpenAttachmentModal(false);
      }
      return res.data
    }).catch(error => {
      toaster("Failed to add attachment", "error")
      setAttachmentsLoading(false);
      setOpenAttachmentModal(false);
    })
  }

  const confirmUpdate = (formData, attachmentId) => {
    setAttachmentsLoading(true);
    return updateAttachment(formData, attachmentId).then(res => {

      // for grid on study details page
      if (attachmentList && setStudyDetails && res.data) {
        setStudyDetails({
          type: 'UPDATE_ITEM',
          itemType: 'attachments',
          item: res.data,
        })
      }

      // for attachment list page
      setRefreshData(true)

      setAttachmentsLoading(false);
      toaster("Attachment updated successfully", "success")
      setOpenAttachmentModal(false)
      return res.data
    }).catch(error => {
      setAttachmentsLoading(false);
      toaster("Failed to update attachment", "error")
      setOpenAttachmentModal(false)
    });
  }

  const confirmDelete = () => {
    setAttachmentsLoading(true)
    deleteAttachment(deleteItem.id).then(res => {

      // for grid on study details page
      if (attachmentList && setStudyDetails) {
        setStudyDetails({
          type: 'DELETE_ITEM',
          itemType: 'attachments',
          id: deleteItem.id,
        })
      }

      // for attachment list page
      setRefreshData(true) // setRefreshData(true)

      setAttachmentsLoading(false)
    })
    setOpenDeleteModal(false)
  }

  /** EVENT HANDLERS */

  const attachmentRowClicked = data => {
    setDetailModalMode("update")
    setAttachmentData(data)
    setAttachmentId(data.id)

    setOpenAttachmentModal(true)
  }

  /** COMPONENTS */

  const addAttachmentsButton = () => {
    return <Button
      kind="primary"
      onClick={() => addNewAttachment()}
      style={addAttachmentBtnStyle}
    >Add Attachment</Button>
  }


  const attachmentsSearch = () => {
    return <div style={{ width: "300px" }}>
      <Search
        size="sm"
        value={search}
        labelText="search"
        onChange={e => setSearch(e.target.value)}
        placeHolderText="Search using any data value" />
    </div>
  }

  const attachmentGrid = () => {
    return <>
      <AttachmentsGrid
        attachmentList={attachmentList}
        setStudyDetails={setStudyDetails}
        search={searchGrid}
        onRowClicked={attachmentRowClicked}
        onDeleteClicked={attachmentRemoveRow}
        setRefresh={setRefreshData}
        refresh={refreshData}
        sort={sort}
        setSort={setSort}
        isStudyDetail={isStudyDetail}
      />
      <AttachmentDetailModal
        mode={detailModalMode}
        attachmentId={attachmentId}
        open={openAttachmentModal}
        attachmentData={attachmentData}
        onClose={() => setOpenAttachmentModal(false)}
        confirmSave={confirmSave}
        confirmUpdate={confirmUpdate}
        attachmentsLoading={attachmentsLoading}
        setAttachmentsLoading={setAttachmentsLoading}
        isStudyDetail={isStudyDetail}
        studyOrgStudyId={isStudyDetail ? studyOrgStudyId : null}
        studyPgId={isStudyDetail ? studyPgId : null}
        studyListOptions={studyListOptions}
      />
      <ConfirmModal
        title="Confirmation"
        content={`Do you want to delete ${deleteItem?.name} from study ${deleteItem?.org_study_id}?`}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={confirmDelete}
      />
    </>
  }

  return {
    addAttachmentsButton,
    attachmentGrid,
    attachmentsSearch,
    attachmentsLoading,
    setAttachmentsLoading,
  }
}

export default useAttachmentsGrid